import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import {
	AccountingEventType,
	RevenueRecognitionPeriod,
	BillingCycleUnit,
	BillingCycleStruct,
	ContractState,
	ContractType,
	Consumer,
	convertCurrencyToSign,
	CellTypes,
	CyclicDate,
	PricingType,
	tableCalculation,
} from '@received/pricing-model';
import { CustomBillingCycle, Icon, InputIcon, NetTermsSelect, PricingModelTableComponentV2, Select, Switch, Tooltip } from '../../..';
import { IssueDatePicker } from '../../../_uiComponents/DatePicker';
import { getValueWithCurrencySign, numberInRange } from '../../../../utils/NumberUtils';
import { TermsAndSettingsV2 } from '../../../TermsAndSettings/TermsAndSettingsV2';
import { AccountPermissionsActionsList, PaymentTimeOptions, SelectItem } from '../../../../types/generalTypes';
import { initialCyclicDay } from './../../PricingModelTabContent.utils';
import { PricingModelTableViewTypeOptions } from '../../../PricingModelTable/PricingModelTable.utils';
import { onAddRowStructure } from '../../../PricingModelTable/PricingModelTableActions';
import { PermissionsMapping } from '../../../../services/PermissionsMapping/PermissionsMapping';
import { Contract, PricingModelTab, RevenueSettings } from '../../../../types/contractTypes';
import { Customer } from '../../../../types/customerTypes';
import { CreditNoteTabSettings } from './CreditNoteTabSettings/CreditNoteTabSettings';
import { InvoiceTabSettingsV2 } from './InvoiceTabSettings/InvoiceTabSettingsV2';
import { RevRecSettings } from './RevRecSettings/RevRecSettings';
import {
	getBillingStartDate,
	onUpdateCurrency,
	updateBillingCycle,
	updatePaymentPeriod,
	updatePricingModelTable,
	updateRevRecPaymentPeriod,
} from '../../PricingModelTabContentActions';
import { ReferralTabSettings } from './ReferralTabSettings/ReferralTabSettings';
import { billingCyclePeriodSelect, DEFAULT_BILLING_CYCLE_VALUE } from '../../../../constants/generalConstants';
import styles from './ContractContentV2.module.scss';

export interface ContractContentProps {
	contract?: Contract;
	selectedCustomer?: Customer;
	pricingModelTab: PricingModelTab;
	isEditable?: boolean;
	className?: string;
	contractType?: ContractType;
	// Flat-rate: single fixed row (no tiers) — hide the "+ Add" footer and per-row remove trash.
	isFlatRate?: boolean;
	// Pricing-model template dropdown (above the price table).
	templateOptions?: SelectItem[];
	templateDefaultValue?: string;
	onSelectTemplate?: (templateId: string) => void;
	// Pay-as-you-go toggle → swap the item between a Fixed and a Usage pricing model (applies the
	// category's "Flat rate" default) and force billing In arrears when turned on.
	onTogglePayAsYouGo?: (isPayAsYouGo: boolean) => void;
	// The Pay-as-you-go toggle is only shown when the item has a matching usage product (that product is
	// what drives the metered quantity). Hidden otherwise.
	canPayAsYouGo?: boolean;
	updatePricingModelData: (data: PricingModelTab, instantUpdate?: boolean) => void;
	onUpdateManualReportFromTable?: () => void;
}

export const ContractContentV2 = ({
	contract,
	selectedCustomer,
	pricingModelTab,
	className,
	isEditable,
	contractType,
	isFlatRate,
	templateOptions,
	templateDefaultValue,
	onSelectTemplate,
	onTogglePayAsYouGo,
	canPayAsYouGo = true,
	updatePricingModelData,
}: ContractContentProps) => {
	const [isCustomBillingCycleOpen, setIsCustomBillingCycleOpen] = useState(false);
	const [advancedOpen, setAdvancedOpen] = useState(false);
	// DOM-measured vertical center of each row so the remove trash lines up with every row.
	const pricingTableWrapperRef = useRef<HTMLDivElement>(null);
	const [rowCenters, setRowCenters] = useState<number[]>([]);
	const notEditableTab =
		(!!contract?.editBlockers?.length &&
			contract.state === ContractState.ACTIVE &&
			!!pricingModelTab?.lastActiveDate &&
			pricingModelTab.billingCycle.unit === BillingCycleUnit.ONE_TIME) ||
		!isEditable;

	useEffect(() => {
		updatePaymentPeriod(pricingModelTab, updatePricingModelData);
	}, [pricingModelTab?.issueDay, pricingModelTab?.billingStartDate, pricingModelTab?.billingEndDate, pricingModelTab?.billingCycle]);

	useEffect(() => {
		updateRevRecPaymentPeriod(pricingModelTab, updatePricingModelData);
	}, [
		pricingModelTab?.revenueSettings?.postingDay,
		pricingModelTab?.revenueSettings?.revenueCycle,
		pricingModelTab?.revenueSettings?.revenueRecognitionPeriod,
	]);

	useEffect(() => {
		const wrapper = pricingTableWrapperRef.current;
		if (!wrapper) return;
		const rowEls = Array.from(wrapper.getElementsByClassName(styles.tableRowContainer)) as HTMLElement[];
		const wrapperTop = wrapper.getBoundingClientRect().top;
		setRowCenters(rowEls.map((row) => row.getBoundingClientRect().top - wrapperTop + row.getBoundingClientRect().height / 2));
	}, [pricingModelTab?.pricingModelTable?.rows, notEditableTab]);

	const updateActivationDates = (startDate: Date | null, endDate: Date | null) => {
		const tempModel = { ...pricingModelTab };
		tempModel.billingStartDate = startDate;
		tempModel.billingEndDate = endDate;

		tempModel.issueDay = getBillingStartDate(tempModel?.billingCycle, startDate, endDate);

		updatePricingModelData(tempModel, true);
	};

	const updateRevRecActivationDates = (revenueSettings: RevenueSettings) => {
		const revenueCycle = pricingModelTab?.revenueSettings?.revenueCycle;
		let postingDay;

		if (revenueCycle?.unit === BillingCycleUnit.QUARTERLY) {
			postingDay = initialCyclicDay;
		} else {
			switch (revenueSettings?.revenueRecognitionPeriod) {
				case RevenueRecognitionPeriod.CONTRACT_ACTIVATION: {
					postingDay = getBillingStartDate(revenueCycle as BillingCycleStruct, contract?.activationStartDate);
					break;
				}
				case RevenueRecognitionPeriod.CUSTOM: {
					postingDay = getBillingStartDate(revenueCycle as BillingCycleStruct, revenueSettings?.recognitionPeriodStart);
					break;
				}
			}
		}

		updatePricingModelData?.(
			{
				...pricingModelTab,
				revenueSettings: { ...pricingModelTab.revenueSettings, ...revenueSettings, postingDay },
			},
			true,
		);
	};

	const getSettingsByAccountingEventType = (accountingEventType?: AccountingEventType) => {
		switch (accountingEventType) {
			// USAGE_INVOICE is an invoice type too — must show Billing Schedule like INVOICE/DEBIT.
			case AccountingEventType.DEBIT:
			case AccountingEventType.INVOICE:
			case AccountingEventType.USAGE_INVOICE:
				return (
					<InvoiceTabSettingsV2
						contract={contract}
						pricingModelTab={pricingModelTab}
						updateActivationDates={updateActivationDates}
						updatePricingModelData={updatePricingModelData}
						updateCurrency={(currency) => onUpdateCurrency(currency, pricingModelTab, updatePricingModelData)}
					/>
				);
			case AccountingEventType.PAYOUT:
				return (
					<ReferralTabSettings
						contract={contract}
						pricingModelTab={pricingModelTab}
						updateActivationDates={updateActivationDates}
						updatePricingModelData={updatePricingModelData}
						updateCurrency={(currency) => onUpdateCurrency(currency, pricingModelTab, updatePricingModelData)}
					/>
				);
			case AccountingEventType.CREDIT:
				return (
					<CreditNoteTabSettings
						contract={contract}
						pricingModelTab={pricingModelTab}
						updateActivationDates={updateActivationDates}
						updatePricingModelData={updatePricingModelData}
						updateCurrency={(currency) => onUpdateCurrency(currency, pricingModelTab, updatePricingModelData)}
					/>
				);
			default:
				break;
		}
	};

	const onUpdateCustomBillingCycle = (billingCycle: BillingCycleStruct) => {
		pricingModelTab &&
			updatePricingModelData?.({
				...pricingModelTab,
				pricingCycle: null,
				billingCycle: { unit: billingCycle?.unit, cycle: billingCycle?.cycle },
			});
	};

	// Remove the row at index, recalc, persist (mirrors the table's own onRemoveRow).
	const lineItemRows = pricingModelTab?.pricingModelTable?.rows || [];
	const onRemoveRow = (index: number) => {
		if (!lineItemRows.length) return;
		const { table } = tableCalculation(
			{ ...pricingModelTab.pricingModelTable, rows: lineItemRows.filter((_, i) => i !== index) },
			Consumer.RECEIVED_UI,
		);
		updatePricingModelTable(table, pricingModelTab, updatePricingModelData);
	};

	// Custom "Add" button replacing the table's built-in "New" row (not exposed via classNames).
	const onAddLineItemRow = () => {
		const withNewRow = onAddRowStructure(pricingModelTab.pricingModelTable);
		// Item is shared across rows; onAddRowStructure blanks it, so copy row 0's item onto the new row.
		const itemColumn = withNewRow.columns.find((col) => col.type === CellTypes.ITEM_REFERENCE);
		const newRowIndex = withNewRow.rows.length - 1;
		if (itemColumn && newRowIndex > 0) {
			const sharedItemCell = withNewRow.rows[0].cells[itemColumn.id];
			const newRow = withNewRow.rows[newRowIndex];
			withNewRow.rows[newRowIndex] = {
				...newRow,
				cells: { ...newRow.cells, [itemColumn.id]: { ...sharedItemCell, cellId: newRow.cells[itemColumn.id]?.cellId } },
			};
		}
		const { table } = tableCalculation(withNewRow, Consumer.RECEIVED_UI);
		updatePricingModelTable(table, pricingModelTab, updatePricingModelData);
	};

	// Pay-as-you-go = usage/metered pricing (postpaid, billed on actual usage). It's the controlling
	// decision: turning it on swaps the item to a Usage pricing model and forces billing In arrears.
	const isPayAsYouGo = pricingModelTab?.pricingType === PricingType.USAGE;
	const togglePayAsYouGo = (checked: boolean) => {
		if (notEditableTab) return;
		if (onTogglePayAsYouGo) {
			onTogglePayAsYouGo(checked);
		} else {
			// Fallback (no container handler): flip the usage axis and its forced timing directly.
			updatePricingModelData(
				{
					...pricingModelTab,
					pricingType: checked ? PricingType.USAGE : PricingType.DEFAULT,
					paymentTime: checked ? PaymentTimeOptions.AFTER_USE : PaymentTimeOptions.DURING_USE,
				},
				true,
			);
		}
	};
	const payAsYouGoToggle = (
		<div className={styles.chargeType}>
			<div className={styles.payAsYouGoRow}>
				<span className={styles.chargeTypeLabel}>Pay as you go</span>
				<Tooltip
					blackTooltip
					placement='top'
					tooltipComponent={
						<div className={styles.advancedTooltip}>Bill at the end of the period based on actual usage (postpaid). Always billed in arrears.</div>
					}
				>
					<Icon imgType='information' color='neutral400' width={1.3} />
				</Tooltip>
				<div className={styles.payAsYouGoSwitch}>
					<Switch smallSize defaultChecked={isPayAsYouGo} defaultDisable={notEditableTab} onChange={togglePayAsYouGo} />
				</div>
			</div>
		</div>
	);

	// "In advance" (prepaid) vs "In arrears" (postpaid) is pure billing timing. Locked to In arrears while
	// Pay-as-you-go is on (usage can't be billed before it happens) and in read-only mode.
	const timingDisabled = notEditableTab || isPayAsYouGo;
	const activeTiming = isPayAsYouGo ? PaymentTimeOptions.AFTER_USE : pricingModelTab?.paymentTime ?? PaymentTimeOptions.DURING_USE;
	const chargeTypeToggle = (
		<div className={styles.chargeType}>
			<span className={styles.chargeTypeLabel}>Charge type</span>
			<div className={styles.chargeTypeToggle}>
				{[
					{
						value: PaymentTimeOptions.DURING_USE,
						label: 'In advance',
						tooltip: 'Bill up front at the start of each billing period (prepaid).',
					},
					{
						value: PaymentTimeOptions.AFTER_USE,
						label: 'In arrears',
						tooltip: 'Bill at the end of each billing period (postpaid).',
					},
				].map((option) => (
					<button
						key={option.value}
						type='button'
						disabled={timingDisabled}
						className={clsx(styles.chargeTypeOption, activeTiming === option.value && styles.chargeTypeOptionActive)}
						onClick={() => updatePricingModelData({ ...pricingModelTab, paymentTime: option.value }, true)}
					>
						{option.label}
						<Tooltip blackTooltip placement='top' tooltipComponent={<div className={styles.advancedTooltip}>{option.tooltip}</div>}>
							<Icon imgType='information' color='neutral400' width={1.3} />
						</Tooltip>
					</button>
				))}
			</div>
			{isPayAsYouGo && <span className={styles.chargeTypeHint}>Pay-as-you-go is always billed in arrears.</span>}
		</div>
	);

	// Currency sign for the Minimum Fee prefix.
	const currencySign = convertCurrencyToSign(pricingModelTab?.currency);
	// Advanced+Summary layout is for invoice-type items; credit/payout keep dedicated settings.
	const isInvoiceType = [AccountingEventType.INVOICE, AccountingEventType.USAGE_INVOICE, AccountingEventType.DEBIT].includes(
		pricingModelTab?.accountingEventType as AccountingEventType,
	);

	return (
		<div className={clsx(notEditableTab && styles.notAllowedCursor)}>
			<div className={clsx(styles.container, notEditableTab && styles.notEditableContract, className)}>
				<div className={styles.pricePanel}>
					{canPayAsYouGo && payAsYouGoToggle}
					{chargeTypeToggle}

					<div className={styles.pricePanelField}>
						<span className={styles.pricePanelLabel}>Pricing model</span>
						<Select
							isSearchable
							menuPlacement='bottom'
							style={{ width: '100%' }}
							disabled={notEditableTab}
							resetSelectOnClose={false}
							placeholder='Select pricing model'
							inputIcon={{ imgType: 'pricing', color: 'neutral400', width: 1.6 }}
							data={templateOptions || []}
							defaultValue={templateDefaultValue}
							onChange={(value) => value && onSelectTemplate?.(value)}
						/>
					</div>

					<div className={styles.pricePanelField}>
						<span className={styles.pricePanelLabel}>Billing cycle</span>
						<Select
							menuPlacement='bottom'
							style={{ width: '100%' }}
							disabled={notEditableTab}
							data={billingCyclePeriodSelect()}
							defaultValue={pricingModelTab?.billingCycle?.unit}
							onChange={(unit) =>
								unit === BillingCycleUnit.CUSTOM
									? setIsCustomBillingCycleOpen(true)
									: updateBillingCycle(
											{ unit: unit as BillingCycleUnit, cycle: DEFAULT_BILLING_CYCLE_VALUE },
											pricingModelTab,
											updatePricingModelData,
											true,
										)
							}
						/>
					</div>

					<div className={styles.pricePanelField}>
						{/* Positioning context for the per-row trash + "+ Add" footer. */}
						<div className={clsx(styles.pricingTableWrapper, !notEditableTab && styles.editableTable)} ref={pricingTableWrapperRef}>
							<PricingModelTableComponentV2
								pricingModelTableViewType={PricingModelTableViewTypeOptions.CONTRACT}
								pricingModelTable={pricingModelTab?.pricingModelTable}
								pricingModelTab={pricingModelTab}
								isEditable={!notEditableTab}
								hidePricingHeader
								hideActionsColumn
								disableFormulaHover
								selectedCustomer={selectedCustomer}
								updateTableData={(table) => updatePricingModelTable(table, pricingModelTab, updatePricingModelData)}
								updatePricingModelData={(data, instantUpdate?: boolean) => updatePricingModelData(data, instantUpdate)}
								classNames={{
									tableContainer: styles.tableSectionContainer,
									table: styles.tableGrid,
									headerContainer: styles.tableHeaderRow,
									headerCell: styles.tableHeaderCell,
									rowContainer: styles.tableRowContainer,
									col: styles.tableCol,
									cell: styles.tableCell,
								}}
							/>

							{!notEditableTab &&
								!isFlatRate &&
								rowCenters.map((center, index) => (
									<button
										type='button'
										key={index}
										className={styles.removeLastRowButton}
										style={{ top: center }}
										onClick={() => onRemoveRow(index)}
										aria-label='Remove line item'
									>
										<Icon imgType='delete' color='neutral400' width={1.7} />
									</button>
								))}

							{!notEditableTab && !isFlatRate && (
								<button type='button' className={styles.addLineItemButton} onClick={onAddLineItemRow}>
									<Icon imgType='add' color='success' width={1.4} />
									<span>Add</span>
								</button>
							)}
						</div>
					</div>
				</div>

				{isInvoiceType ? (
					<>
						<div className={styles.advancedSection}>
							<button type='button' className={styles.advancedHeader} aria-expanded={advancedOpen} onClick={() => setAdvancedOpen((open) => !open)}>
								<span className={styles.advancedTitle}>Advanced</span>
								<span className={clsx(styles.advancedChevron, !advancedOpen && styles.advancedChevronCollapsed)}>
									<Icon imgType='arrow_down' color='neutral400' width={1.4} />
								</span>
							</button>

							{advancedOpen && (
								<div className={styles.advancedBody}>
									{/* Invoice date is per line item, derived from this item's billing cycle. */}
									<div className={styles.advancedField}>
										<span className={styles.advancedLabel}>Invoice date</span>
										<div className={styles.advancedInputIcon}>
											<IssueDatePicker
												disabled={notEditableTab}
												customHeaderByBillingCycle={pricingModelTab?.billingCycle}
												defaultIssueDate={pricingModelTab?.issueDay}
												className={styles.embeddedDatePicker}
												updateIssueDate={(date: CyclicDate) => updatePricingModelData({ ...pricingModelTab, issueDay: date || undefined })}
											/>
										</div>
									</div>

									{/* Payment Terms is a contract-wide default; editable here only when Separate Invoice is ON (isCollective false). */}
									{pricingModelTab?.isCollective === false && (
										<div className={styles.advancedField}>
											<span className={styles.advancedLabel}>Payment Terms</span>
											<div className={styles.advancedInputIcon}>
												<NetTermsSelect
													style={{ width: '100%' }}
													disabled={notEditableTab}
													defaultValue={pricingModelTab?.netTerms}
													isUsageProduct={pricingModelTab?.pricingType == PricingType.USAGE}
													onChange={(netTerms: any) => updatePricingModelData({ ...pricingModelTab, netTerms }, true)}
												/>
											</div>
										</div>
									)}

									<div className={styles.advancedField}>
										<span className={styles.advancedLabel}>Minimum Fee</span>
										<InputIcon
											value={pricingModelTab?.minimumFee ?? ''}
											prefixSign={currencySign}
											type='number'
											className={styles.advancedInputIcon}
											inputContainerClassName={styles.advancedInputField}
											onChange={(val) => updatePricingModelData({ ...pricingModelTab, minimumFee: +val })}
										/>
									</div>

									<div className={styles.advancedField}>
										<span className={styles.advancedLabel}>Discount</span>
										<InputIcon
											value={pricingModelTab?.discount ?? ''}
											prefixSign='%'
											type='number'
											className={styles.advancedInputIcon}
											inputContainerClassName={styles.advancedInputField}
											onChange={(val) => updatePricingModelData({ ...pricingModelTab, discount: numberInRange(+val, 0, 100) })}
										/>
									</div>

									<div className={styles.advancedField}>
										<span className={styles.advancedLabel}>Tax</span>
										<InputIcon
											value={pricingModelTab?.tax ?? ''}
											prefixSign='%'
											type='number'
											className={styles.advancedInputIcon}
											inputContainerClassName={styles.advancedInputField}
											onChange={(val) => updatePricingModelData({ ...pricingModelTab, tax: numberInRange(+val, 0, 100) })}
										/>
									</div>

									<div className={styles.advancedField}>
										<div className={styles.advancedLabelWithInfo}>
											<span className={styles.advancedLabel}>Separate Invoice</span>
											<Tooltip
												blackTooltip
												placement='top'
												tooltipComponent={
													<div className={styles.advancedTooltip}>
														Bill this line item on its own invoice instead of combining it with the other line items in this contract.
													</div>
												}
											>
												<Icon imgType='information' color='neutral400' width={1.4} />
											</Tooltip>
										</div>
										<Switch
											onChange={(separateInvoice: boolean) => updatePricingModelData({ ...pricingModelTab, isCollective: !separateInvoice }, true)}
											defaultChecked={pricingModelTab?.isCollective === false}
										/>
									</div>

									{contractType != ContractType.REFERRAL && (
										<PermissionsMapping actionType={AccountPermissionsActionsList.VIEW_REV_REC}>
											<RevRecSettings
												pricingModelTab={pricingModelTab}
												updatePricingModelData={(data, instantUpdate?: boolean) => updatePricingModelData(data, instantUpdate)}
												updateActivationDates={updateRevRecActivationDates}
												className={styles.sectionContainer}
											/>
										</PermissionsMapping>
									)}
								</div>
							)}
						</div>
					</>
				) : (
					<div className={styles.settingsSummaryRow}>
						<div className={styles.settingsColumn}>
							{getSettingsByAccountingEventType(pricingModelTab?.accountingEventType)}

							{contractType != ContractType.REFERRAL && (
								<PermissionsMapping actionType={AccountPermissionsActionsList.VIEW_REV_REC}>
									<RevRecSettings
										pricingModelTab={pricingModelTab}
										updatePricingModelData={(data, instantUpdate?: boolean) => updatePricingModelData(data, instantUpdate)}
										updateActivationDates={updateRevRecActivationDates}
										className={styles.sectionContainer}
									/>
								</PermissionsMapping>
							)}
						</div>

						<div className={styles.summaryColumn}>
							<TermsAndSettingsV2
								pricingModelTab={pricingModelTab}
								customerId={selectedCustomer?.id}
								updatePricingTermsData={({ note, supplierId, billingDetails, cryptoSettings }, instantUpdate?: boolean) => {
									updatePricingModelData(
										{
											...pricingModelTab,
											billingDetails: { ...pricingModelTab.billingDetails, ...billingDetails },
											cryptoSettings: { ...pricingModelTab.cryptoSettings, ...cryptoSettings },
											supplierId,
											note,
										},
										instantUpdate,
									);
								}}
								updatePricingSummaryData={({ tax, discount, minimumFee }) =>
									updatePricingModelData({ ...pricingModelTab, tax, discount, minimumFee: minimumFee ?? pricingModelTab.minimumFee })
								}
								updateCreditSettings={({ note, supplierId, billingDetails }) => {
									updatePricingModelData({
										...pricingModelTab,
										note,
										supplierId,
										billingDetails: { ...pricingModelTab.billingDetails, ...billingDetails },
									});
								}}
							/>
						</div>
					</div>
				)}

				<CustomBillingCycle
					isOpen={isCustomBillingCycleOpen}
					billingCycle={pricingModelTab?.billingCycle}
					onUpdateCustomBillingCycle={onUpdateCustomBillingCycle}
					onClose={() => setIsCustomBillingCycleOpen(false)}
				/>
			</div>
		</div>
	);
};
